import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { useLocations } from '@/hooks/use-locations';
import {
  formatMinor,
  useKitchenOrder,
  useKitchenOrders,
  type KitchenOrderStatus,
  type KitchenOrderSummary,
} from '@/hooks/use-kitchen';
import { useT } from '@/i18n/use-t';

/** The shape `useT` returns, for the helpers below that cannot call a hook. */
type Translate = (key: string, bakedEnglish?: string) => string;

const STATUSES: KitchenOrderStatus[] = [
  'queued',
  'preparing',
  'ready',
  'collected',
  'cancelled',
  'noShow',
];

const STATUS_LABEL: Record<KitchenOrderStatus, string> = {
  queued: 'Queued',
  preparing: 'Preparing',
  ready: 'Ready',
  collected: 'Collected',
  cancelled: 'Cancelled',
  noShow: 'No-show',
};

/** Catalogue key per status — declared here so the parity guard can see them. */
const STATUS_KEY: Record<KitchenOrderStatus, string> = {
  queued: 'terminals.kitchen.statusQueued',
  preparing: 'terminals.kitchen.statusPreparing',
  ready: 'terminals.kitchen.statusReady',
  collected: 'terminals.kitchen.statusCollected',
  cancelled: 'terminals.kitchen.statusCancelled',
  noShow: 'terminals.kitchen.statusNoShow',
};

function statusLabel(status: KitchenOrderStatus, t: Translate): string {
  return t(STATUS_KEY[status], STATUS_LABEL[status]);
}

function relative(iso: string, t: Translate): string {
  const seconds = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  const ago = (key: string, en: string, n: number) => t(key, en).replace('{n}', String(n));
  if (seconds < 60) return ago('terminals.relative.secondsAgo', '{n}s ago', seconds);
  if (seconds < 3600)
    return ago('terminals.relative.minAgo', '{n} min ago', Math.floor(seconds / 60));
  if (seconds < 86_400)
    return ago('terminals.relative.hAgo', '{n} h ago', Math.floor(seconds / 3600));
  return ago('terminals.relative.dAgo', '{n} d ago', Math.floor(seconds / 86_400));
}

function statusVariant(status: KitchenOrderStatus): 'default' | 'success' | 'outline' {
  if (status === 'queued' || status === 'preparing') return 'default';
  if (status === 'ready') return 'success';
  return 'outline';
}

/**
 * Kitchen station orders per location (FN-93, ADR-0013). Read-only: the
 * station is the system of record and BOS mirrors what it uploaded — no
 * commands go back. Root-cause visibility Fayno's IT asked for (FN-58):
 * which orders a store received, where each one stands, when it moved.
 */
export function KitchenOrdersPage() {
  const t = useT('admin_ui');
  const [locationId, setLocationId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);
  const locations = useLocations();
  const orders = useKitchenOrders({
    locationId: locationId || undefined,
    status: (status || undefined) as KitchenOrderStatus | undefined,
    page,
    pageSize: 50,
  });
  const detail = useKitchenOrder(selected);
  const total = orders.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">
            {t('terminals.kitchen.title', 'Kitchen orders')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              'terminals.kitchen.help',
              'What each kitchen station received and where every order stands. Read-only — the station is the system of record; this page shows what it reported.',
            )}
          </p>
        </div>
        <Link className="text-sm underline" to="/terminals">
          {t('terminals.backToTerminals', 'Back to terminals')}
        </Link>
      </header>

      <div className="flex flex-wrap items-end gap-3 rounded-md border bg-card p-3">
        <div className="min-w-56">
          <Label htmlFor="kitchen-location">{t('terminals.field.location', 'Location')}</Label>
          <Select
            id="kitchen-location"
            value={locationId}
            onChange={(e) => {
              setLocationId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">{t('terminals.kitchen.allLocations', 'All locations')}</option>
            {(locations.data?.items ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.code} — {l.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="min-w-40">
          <Label htmlFor="kitchen-status">{t('terminals.field.status', 'Status')}</Label>
          <Select
            id="kitchen-status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">{t('terminals.kitchen.anyStatus', 'Any status')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {statusLabel(s, t)}
              </option>
            ))}
          </Select>
        </div>
        <div className="ml-auto text-sm text-muted-foreground" aria-live="polite">
          {orders.isLoading
            ? t('common.loading', 'Loading…')
            : (total === 1
                ? t('terminals.kitchen.countOne', '{count} order')
                : t('terminals.kitchen.countMany', '{count} orders')
              ).replace('{count}', String(total))}
        </div>
      </div>

      {orders.error !== null && orders.error !== undefined && (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {t('terminals.kitchen.loadError', 'Could not load kitchen orders.')}
        </p>
      )}

      <div className="overflow-x-auto rounded-md border bg-card">
        <Table>
          <THead>
            <TR>
              <TH>{t('terminals.kitchen.colNumber', 'No.')}</TH>
              <TH>{t('terminals.kitchen.colSource', 'Source')}</TH>
              <TH>{t('terminals.field.status', 'Status')}</TH>
              <TH>{t('terminals.kitchen.colPayment', 'Payment')}</TH>
              <TH>{t('terminals.kitchen.colCustomer', 'Customer')}</TH>
              <TH>{t('terminals.kitchen.colPickup', 'Pickup')}</TH>
              <TH>{t('terminals.kitchen.colTotal', 'Total')}</TH>
              <TH>{t('terminals.kitchen.colStation', 'Station')}</TH>
              <TH>{t('terminals.kitchen.colUpdated', 'Updated')}</TH>
            </TR>
          </THead>
          <TBody>
            {(orders.data?.orders ?? []).map((o: KitchenOrderSummary) => (
              <TR
                key={o.id}
                className="cursor-pointer hover:bg-muted/40"
                onClick={() => setSelected(o.id)}
                data-testid={`kitchen-order-${o.displayNumber}`}
              >
                <TD className="font-mono">{o.displayNumber}</TD>
                <TD>
                  <Badge variant="outline">{o.source}</Badge>
                </TD>
                <TD>
                  <Badge variant={statusVariant(o.status)}>{statusLabel(o.status, t)}</Badge>
                  {o.lastError !== null && (
                    <span className="ml-2 text-xs text-destructive" title={o.lastError}>
                      {t('terminals.kitchen.error', 'error')}
                    </span>
                  )}
                </TD>
                <TD>{o.paymentState}</TD>
                <TD>{o.customerLabel ?? '—'}</TD>
                <TD>
                  {new Date(o.pickupAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </TD>
                <TD className="font-mono">{formatMinor(o.totalMinor, o.currency)}</TD>
                <TD>{o.terminalCode ?? o.terminalId}</TD>
                <TD title={o.updatedAt}>{relative(o.updatedAt, t)}</TD>
              </TR>
            ))}
            {orders.data !== undefined && orders.data.orders.length === 0 && (
              <TR>
                <TD colSpan={9} className="text-center text-sm text-muted-foreground">
                  {t('terminals.kitchen.empty', 'No kitchen orders reported yet.')}
                </TD>
              </TR>
            )}
          </TBody>
        </Table>
      </div>

      {pages > 1 && (
        <div className="flex items-center gap-2 text-sm">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            {t('terminals.pagination.previous', 'Previous')}
          </Button>
          <span>
            {t('terminals.pagination.page', 'Page {page} of {pages}')
              .replace('{page}', String(page))
              .replace('{pages}', String(pages))}
          </span>
          <Button variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            {t('terminals.pagination.next', 'Next')}
          </Button>
        </div>
      )}

      {selected !== null && (
        <section className="rounded-md border bg-card p-4" data-testid="kitchen-order-detail">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-medium">
              {t('terminals.kitchen.orderHeading', 'Order {number}').replace(
                '{number}',
                detail.data?.order.displayNumber ?? '',
              )}{' '}
              <span className="font-mono text-xs text-muted-foreground">
                {detail.data?.order.orderNumber ?? ''}
              </span>
            </h2>
            <Button variant="outline" onClick={() => setSelected(null)}>
              {t('common.close', 'Close')}
            </Button>
          </div>
          {detail.data !== undefined && (
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <h3 className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                  {t('terminals.kitchen.lines', 'Lines')}
                </h3>
                <ul className="text-sm">
                  {detail.data.order.lines.map((l) => (
                    <li key={`${l.productId}-${l.name}`} className="flex justify-between">
                      <span>
                        {l.quantity} × {l.name}
                      </span>
                      <span className="font-mono">
                        {formatMinor(l.unitPriceMinor * l.quantity, detail.data!.order.currency)}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-sm">
                  {t(
                    'terminals.kitchen.stickerLine',
                    'Stickers printed: {printed} · Provider status: {status} · Store {store}',
                  )
                    .replace('{printed}', String(detail.data.order.stickersPrinted))
                    .replace('{status}', detail.data.order.providerStatus ?? '—')
                    .replace('{store}', String(detail.data.order.storeId))}
                </p>
              </div>
              <div>
                <h3 className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                  {t('terminals.kitchen.transitions', 'Transitions')}
                </h3>
                <ol className="text-sm">
                  {/* Every order write on the station is mirrored as a row with
                      actor "snapshot" (durable recovery); only real
                      transitions are worth a line here. */}
                  {detail.data.events
                    .filter((e) => e.actor !== 'snapshot' || e.fromStatus !== e.toStatus)
                    .map((e) => (
                      <li key={e.id} className="flex justify-between gap-3">
                        <span>
                          {e.fromStatus === null ? '' : `${statusLabel(e.fromStatus, t)} → `}
                          {statusLabel(e.toStatus, t)}{' '}
                          <span className="text-muted-foreground">
                            {t('terminals.kitchen.by', 'by {actor}').replace('{actor}', e.actor)}
                          </span>
                          {e.note !== null && (
                            <span className="text-muted-foreground"> · {e.note}</span>
                          )}
                        </span>
                        <span
                          className="font-mono text-xs text-muted-foreground"
                          title={e.occurredAt}
                        >
                          {new Date(e.occurredAt).toLocaleTimeString()}
                        </span>
                      </li>
                    ))}
                </ol>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
